import React, { useState, useEffect } from 'react';
import { profilesApi } from '../../api/profiles';
import { battlePersonApi } from '../../api/battlePerson';
import HrnbModal from '../controls/HrnbModal';
import HrnbButton from '../controls/HrnbButton';
import HrnbInput from '../controls/HrnbInput';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbSearch from '../controls/HrnbSearch';
import '../../css/components/BattleChatAddParticipantModal.css';

/**
 * Модальное окно для приглашения участников/жюри в баттл
 */
export default function InviteParticipantModal({
  isOpen,
  onClose,
  battleId,
  onSuccess
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedRole, setSelectedRole] = useState('PARTICIPANT');

  // Загрузка списка профилей
  useEffect(() => {
    if (isOpen) {
      loadProfiles();
    }
  }, [isOpen]);

  // Фильтрация по поиску
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProfiles(profiles);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = profiles.filter(p => {
      const displayName = (p.displayName || '').toLowerCase();
      const username = (p.username || '').toLowerCase();
      const nickname = (p.nickname || '').toLowerCase();
      
      return displayName.includes(query) || username.includes(query) || nickname.includes(query);
    });

    setFilteredProfiles(filtered);
  }, [searchQuery, profiles]);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await profilesApi.list({ size: 1000 });
      // API v2 возвращает { content: [...], page: {...} }
      const profilesList = Array.isArray(data?.content)
        ? data.content
        : Array.isArray(data) ? data : [];
      setProfiles(profilesList);
      setFilteredProfiles(profilesList);
    } catch (err) {
      console.error('[InviteParticipantModal] Failed to load profiles:', err);
      setError('Не удалось загрузить список пользователей');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (profile) => {
    try {
      setInviting(true);
      setError(null);
      setSuccessMessage(null);

      const payload = {
        battleId,
        profileId: profile.id,
        role: selectedRole
      };

      console.log('[InviteParticipantModal] Приглашение:', payload);

      await battlePersonApi.invite(payload);

      setSuccessMessage(`"${profile.displayName || profile.username}" приглашён(а) как ${selectedRole === 'JUDGE' ? 'жюри' : 'участник'}`);

      // Обновляем список (убираем приглашённого)
      setProfiles(prev => prev.filter(p => p.id !== profile.id));
      setFilteredProfiles(prev => prev.filter(p => p.id !== profile.id));

      // Уведомляем родителя об успехе
      if (onSuccess) {
        setTimeout(() => {
          onSuccess({ profileId: profile.id, role: selectedRole });
        }, 1000);
      }
    } catch (err) {
      console.error('[InviteParticipantModal] Failed to invite:', err);
      setError(
        err.response?.status === 409
          ? 'Пользователь уже в баттле'
          : 'Не удалось пригласить: ' + err.message
      );
    } finally {
      setInviting(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setError(null);
    setSuccessMessage(null);
    setSelectedRole('PARTICIPANT');
    onClose();
  };

  return (
    <HrnbModal
      open={isOpen}
      onClose={handleClose}
      title="Пригласить в баттл"
      size="lg"
    >
      <div className="battle-chat-add-participant-modal">
        {/* Сообщения об ошибках/успехе */}
        {error && (
          <HrnbAlert variant="danger" onClose={() => setError(null)} className="mb-3">
            {error}
          </HrnbAlert>
        )}

        {successMessage && (
          <HrnbAlert variant="success" onClose={() => setSuccessMessage(null)} className="mb-3">
            {successMessage}
          </HrnbAlert>
        )}

        {/* Выбор роли */}
        <div className="mb-3">
          <label className="filter-label">Роль:</label>
          <HrnbSelect
            value={selectedRole}
            onChange={setSelectedRole}
            options={[
              { value: 'PARTICIPANT', label: '🎤 Участник' },
              { value: 'JUDGE', label: '⚖️ Жюри' },
            ]}
            className="filter-select"
          />
        </div>

        {/* Поиск */}
        <div className="mb-3">
          <HrnbSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск пользователя по имени или nickname"
            disabled={loading || inviting}
            autoFocus
          />
        </div>

        {/* Статус */}
        <div className="participant-stats mb-3">
          <span className="text-muted">
            Доступно для приглашения: {filteredProfiles.length}
          </span>
        </div>

        {/* Список пользователей */}
        {loading ? (
          <div className="text-center py-4">
            <HrnbLoader size="sm" text="Загрузка пользователей..." />
          </div>
        ) : (
          <>
            {/* Список пользователей */}
            {filteredProfiles.length > 0 && (
              <div className="participants-list">
                {filteredProfiles.map((profile) => (
                  <div
                    key={profile.id}
                    className="participant-item"
                  >
                    <div className="participant-info">
                      <div className="participant-name">
                        {profile.displayName || profile.username || 'Аноним'}
                      </div>
                      {profile.username && (
                        <div className="participant-nickname text-muted">
                          @{profile.username}
                        </div>
                      )}
                      {profile.city && (
                        <div className="participant-status text-muted">
                          📍 {profile.city}
                        </div>
                      )}
                    </div>
                    <HrnbButton
                      variant="primary"
                      size="sm"
                      onClick={() => handleInvite(profile)}
                      disabled={inviting}
                    >
                      {inviting ? <HrnbLoader size="sm" /> : 'Пригласить'}
                    </HrnbButton>
                  </div>
                ))}
              </div>
            )}

            {/* Нет результатов */}
            {filteredProfiles.length === 0 && (
              <div className="text-center py-4">
                <p className="text-muted">
                  {searchQuery
                    ? 'Пользователи не найдены'
                    : 'Нет доступных пользователей'}
                </p>
              </div>
            )}
          </>
        )}

        {/* Кнопки управления */}
        <div className="modal-actions mt-4">
          <HrnbButton
            variant="secondary"
            onClick={handleClose}
            disabled={inviting}
          >
            Закрыть
          </HrnbButton>
        </div>
      </div>
    </HrnbModal>
  );
}
